import { CoverageType } from '@/models/insuranceCoverage'
import { getActiveQuote, type InsuranceIndication } from '@/models/insuranceIndication'
import type { InsuranceQuote } from '@/models/insuranceQuote'
import {
  type InsuranceCoverage,
  InsuranceIndicationStatus,
  type InsuranceOneTimeFee,
  InsuranceQuoteStatus,
  Vendor,
} from '@/models/insuranceTypes'
import { groupQuoteCards } from './groupQuoteCards'
import {
  type CarrierRating,
  type ClaimsCapabilities,
  type CoverageQuote,
  type FinancingOffer,
  IndicationStatus,
  ItemizedCheckoutStyle,
  type MinimalQuoteCardStage,
  type OneTimeFee,
  PricingKind,
  type QuoteCardData,
  QuoteCardStage,
  type QuoteDocument,
  stageFromIndicationStatus,
  stageFromQuoteStatus,
} from './quoteCardTypes'

/**
 * DRF serializes DecimalFields as strings by default (COERCE_DECIMAL_TO_STRING),
 * so premium/limit/deductible arrive as e.g. "15945.00" despite being typed as
 * numbers. Summing those with `+` concatenates and the card's total renders NaN,
 * so coerce at the boundary rather than trusting the declared type.
 */
function toNumber(value: number | string | null | undefined): number {
  const parsed = Number(value ?? 0)
  return Number.isFinite(parsed) ? parsed : 0
}

/**
 * Same coercion, but preserves "the carrier never told us" as null instead of
 * flattening it to a $0 the card would print as a real term.
 */
function toNullableNumber(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function toCoverageQuotes(coverages: InsuranceCoverage[], indication: InsuranceIndication): CoverageQuote[] {
  return coverages.map((coverage) => ({
    type: coverage.coverageType as CoverageType,
    premium: toNullableNumber(coverage.premium),
    limit: toNullableNumber(coverage.limit),
    deductible: toNullableNumber(coverage.deductible),
    comprehensiveDeductible: toNullableNumber(coverage.comprehensiveDeductible),
    indicationId: indication.id,
    ...(coverage.premiumRange && { premiumRange: coverage.premiumRange }),
    ...(coverage.coverageType === CoverageType.AUTO_PHYSICAL_DAMAGE &&
      indication.physicalDamageUnitCount && { physicalDamageUnitCount: indication.physicalDamageUnitCount }),
  }))
}

/** This row's quote PDF, if it has one. Off the indication, not its quote: a quote edit
 *  must not restamp the document's date. */
function toQuoteDocuments(indication: InsuranceIndication): QuoteDocument[] {
  const url = indication.quoteDocument?.url ?? indication.sourceDocumentUrl
  if (!url) return []
  const addedAt =
    indication.quoteDocument?.dateCreated ?? (indication.sourceDocumentUrl ? indication.dateUpdated : null)
  return [{ url, addedAt }]
}

function toOneTimeFees(fees: InsuranceOneTimeFee[] | undefined, indicationId: string): OneTimeFee[] {
  // Declared required on InsuranceIndication, but older payloads omit it entirely.
  return (fees ?? []).map((fee) => ({
    id: fee.id,
    label: fee.label,
    amount: toNumber(fee.amount),
    indicationId,
  }))
}

function toClaimsCapabilities(indication: InsuranceIndication): ClaimsCapabilities | undefined {
  const claims = indication.claimsCapabilities
  if (!claims) return undefined
  return {
    hasMultipleWaysToFile: claims.hasMultipleWaysToFile,
    waysToFile: claims.waysToFile,
    has247Support: claims.has247Support,
    supportAvailability: claims.supportAvailability,
    hasPayoutInformation: claims.hasPayoutInformation,
    payoutSpeed: claims.payoutSpeed,
    waysToGetPaid: claims.waysToGetPaid,
  }
}

/**
 * Financing is curated as one free-text description on the carrier record; having any
 * text at all is what "financing available" means, so there's no separate flag to read.
 */
function toFinancingOffer({
  financingOptions,
  paymentPlans,
}: InsuranceIndication): FinancingOffer | undefined {
  // The carrier's own plans take the canned text's place.
  if (paymentPlans?.plans.length) {
    return {
      isStandard: true,
      plans: paymentPlans.plans,
      ...(paymentPlans.note && { plansNote: paymentPlans.note }),
    }
  }
  if (!financingOptions) return undefined
  return { isStandard: true, description: financingOptions }
}

/** Quote statuses whose row carries the carrier's own numbers — every other one is written with no coverages. */
const FAILED_STAGES = [
  QuoteCardStage.ERROR,
  QuoteCardStage.DECLINED,
  QuoteCardStage.UNAVAILABLE,
] as const satisfies readonly MinimalQuoteCardStage[]

const PRICED_QUOTE_STATUSES: ReadonlySet<InsuranceQuoteStatus> = new Set([
  InsuranceQuoteStatus.QUOTED,
  InsuranceQuoteStatus.REQUEST_TO_BIND,
  InsuranceQuoteStatus.BOUND,
])

/** A quote row in one of these is a failed attempt, not an offer the customer holds. */
const FAILED_QUOTE_STATUSES: ReadonlySet<InsuranceQuoteStatus> = new Set([
  InsuranceQuoteStatus.ERROR,
  InsuranceQuoteStatus.DECLINED,
])

/**
 * An indication that has produced a quote is represented by that quote — the quote is the
 * firm version of the same carrier's offer, so the card shows one, not both.
 */
function toQuoteCard(
  indication: InsuranceIndication,
  brokerName?: string,
  carrierTermsAvailable = true,
  sourcedThrough?: string
): QuoteCardData {
  const quote: InsuranceQuote | null = getActiveQuote(indication)
  const source = quote ?? indication
  // A failed quote row is written with no coverages, so the indication stays the price.
  const pricedQuote = quote && PRICED_QUOTE_STATUSES.has(quote.status) ? quote : null
  const pricing = pricedQuote ?? indication
  // Any quote row that isn't a failed attempt makes this a quote, not one of our estimates.
  const isEstimate = !indication.isCustomerUploaded && (!quote || FAILED_QUOTE_STATUSES.has(quote.status))
  const hasBeenSubmitted = !!indication.dateSubmitted
  // PENDING is only ever create_manual_quote's placeholder, never a carrier still working.
  const quotedStage =
    quote && quote.status !== InsuranceQuoteStatus.PENDING
      ? stageFromQuoteStatus(quote.status, hasBeenSubmitted)
      : stageFromIndicationStatus(indication.status, hasBeenSubmitted)
  // Our own prequote rather than the carrier's price. Every carrier result lands as
  // GENERATED, so premiums on an indication that is anything else were put there by
  // us — which is what keeps the cart blocked on an estimate priced without the
  // documents the carrier is still waiting for.
  const isOurEstimate =
    isEstimate &&
    indication.status !== InsuranceIndicationStatus.GENERATED &&
    pricing.coverages.some((coverage) => coverage.premium)
  // The stage the estimate is standing in front of, so a card showing a price still
  // says the quoting behind it failed. Carried as the stage rather than the carrier's
  // own words: this card is customer-facing, and the stage already has copy for it.
  // Declined, errored or unavailable: the carrier is done, whatever it left behind.
  const carrierHasAnswered = (FAILED_STAGES as readonly QuoteCardStage[]).includes(quotedStage)
  const failedStage =
    isOurEstimate && carrierHasAnswered ? (quotedStage as (typeof FAILED_STAGES)[number]) : null
  // The customer already picked this price; a quote moving on to bind must not blank it.
  const stage =
    (indication.dateApplied && pricing.coverages.length) || isOurEstimate
      ? QuoteCardStage.PRICED
      : quotedStage

  const base = {
    id: indication.id,
    vendor: indication.vendor,
    offerGroup: indication.offerGroup,
    coverageLine: indication.coverageLine,
    needsAttestation:
      !carrierHasAnswered && isEstimate && !!indication.needsAttestation && carrierTermsAvailable,
    // On the base, not the priced branch: a merged minimal card needs it too.
    memberIds: [indication.id],
    // Off `source`, like the name and paper below: staff can repoint a quote's carrier
    // without touching its indication, and the group key must match what the card shows.
    carrierId: source.carrierId,
    carrierName: source.vendorName,
    carrierBrand: source.carrierBrand,
    iconUrl: source.iconUrl,
    iconAlt: source.iconAlt,
    carrierRating: (indication.carrierRating as CarrierRating | null) ?? undefined,
    coiSpeed: indication.coiSpeed ?? undefined,
    carrierPublicNote: indication.carrierPublicNote ?? undefined,
    carrierProgram: source.carrierProgram ?? undefined,
    insuranceType: source.insuranceType ?? undefined,
    outstandingDocuments: indication.outstandingDocuments ?? [],
    lossRunStale: !!indication.metadata?.corgiLossRunStale,
    sourcedThrough,
    failureReason: carrierHasAnswered ? (indication.failureReason ?? null) : null,
    // Corgi state its sync run stores. Optional chains: the public payload omits metadata.
    carrierExpired: !!indication.metadata?.corgiExpired,
    expiresAt: indication.metadata?.corgiQuoteDates?.expiresAt ?? null,
  }

  if (stage !== QuoteCardStage.PRICED) {
    return { ...base, stage }
  }

  return {
    ...base,
    stage,
    // dateCreated, not dateUpdated: it dates the dispatch, which is what the round is.
    supersededAt: indication.status === InsuranceIndicationStatus.CANCELED ? indication.dateCreated : null,
    isCurrentInsurer: indication.isCurrentInsurer,
    // Read off the indication, never `source`: the choice is stamped on the indication, not its quote.
    appliedAt: indication.dateApplied || null,
    isCustomerUploaded: indication.isCustomerUploaded,
    uploadedAt: indication.isCustomerUploaded ? indication.dateCreated : undefined,
    financing: toFinancingOffer(indication),
    claimsCapabilities: toClaimsCapabilities(indication),
    brokerName,
    pricingKind: isEstimate ? PricingKind.INDICATION : PricingKind.QUOTED,
    // Only an indication carries a status; a quote is already the carrier's firm answer.
    // Nothing is outstanding once the carrier has answered — is_manual_review stays
    // set on the indication after a decline lands, so reading it alone put "underwriters
    // are reviewing your information" directly under "declined to provide quotes".
    // Terms first among the rest: that one the customer can act on, where a review
    // only asks them to wait.
    indicationStatus: carrierHasAnswered
      ? null
      : isEstimate && indication.needsAttestation && carrierTermsAvailable
        ? IndicationStatus.NEEDS_ATTESTATION
        : isEstimate && indication.metadata?.isManualReview
          ? IndicationStatus.REVIEWING_SUBMITTED_INFO
          : null,
    // Left unset for an uploaded quote: an expiry we don't hold would be an invented one.
    quotedAt: pricedQuote ? pricedQuote.dateCreated : null,
    // Design 2 is the only itemized style the product uses; design 1 stays prototype-only.
    itemizedCheckoutStyle:
      !isEstimate && indication.allowsIndividualCoveragePurchase
        ? ItemizedCheckoutStyle.CAN_PURCHASE_INDIVIDUAL
        : undefined,
    // The indication, not `pricing`: the cart key must name a row the backend can stamp.
    coverages: toCoverageQuotes(pricing.coverages, indication),
    totalPremium: toNumber(pricing.premium),
    totalPremiumRange: isOurEstimate ? (indication.premiumRange ?? null) : null,
    isEstimateOnly: isOurEstimate,
    failedStage,
    oneTimeFees: toOneTimeFees(pricing.oneTimeFees, indication.id),
    brokerNote: source.carrierNote ? { note: source.carrierNote } : null,
    // Off `source`, like the note: a quote carries the flag its indication was created with.
    brokerRecommended: source.brokerRecommended,
    recommendedByName: source.brokerRecommendedByName ?? undefined,
    quoteDocuments: toQuoteDocuments(indication),
  }
}

/** The brand a card will wear, read off the same record {@link toQuoteCard} names it from. */
function brandOf(indication: InsuranceIndication): string | null {
  return (getActiveQuote(indication) ?? indication).carrierBrand
}

/**
 * "Sourced through Rocklake", only where the same brand also came direct from Progressive.
 * Rocklake's Nirvana sits beside our own Nirvana today, and needs no label.
 */
function sourcedThroughByIndication(indications: InsuranceIndication[]): Map<string, string> {
  const vendorsByBrand = new Map<string, Set<Vendor>>()
  for (const indication of indications) {
    const brand = brandOf(indication)
    if (!brand) continue
    const vendors = vendorsByBrand.get(brand) ?? new Set<Vendor>()
    vendors.add(indication.vendor)
    vendorsByBrand.set(brand, vendors)
  }
  const labels = new Map<string, string>()
  for (const indication of indications) {
    const brand = brandOf(indication)
    if (indication.vendor !== Vendor.ROCKLAKE || !brand || !indication.broker) continue
    if (vendorsByBrand.get(brand)?.has(Vendor.PROGRESSIVE)) labels.set(indication.id, indication.broker)
  }
  return labels
}

/**
 * Builds the quotes-page card list from the application payload.
 *
 * Every indication becomes a card first and visibility is applied to the result: a Rocklake
 * decline is withheld from the customer on its own, but it still has to reach the card its
 * priced sibling ends up on, and filtering first would drop it before grouping ever saw it.
 * A card survives if any of the indications behind it is one the viewer may see — pending
 * carriers always are, so the customer sees work in flight.
 *
 * `application.estimates` is deliberately excluded — those are the anonymous DOT-only funnel's
 * synthetic per-truck ranges, which never occur in the authenticated application flow and which
 * the card has no way to express (it renders a single premium, not a range).
 *
 * A carrier that answered on several separately-submitted lines is folded into one card — see
 * {@link groupQuoteCards}.
 */
export function applicationToQuoteCards(
  indications: InsuranceIndication[],
  brokerName?: string,
  /** False suppresses the terms banner: the carrier's templates are unavailable, so
   * Review would open on nothing. */
  carrierTermsAvailable = true
): QuoteCardData[] {
  const visibleIds = new Set(
    indications
      .filter(
        (indication) => indication.status === InsuranceIndicationStatus.PENDING || indication.isCardVisible
      )
      .map((indication) => indication.id)
  )
  const cards = indicationsToOfferCards(indications, carrierTermsAvailable, brokerName)
  return groupQuoteCards(cards).filter((card) =>
    (card.memberIds ?? [card.id]).some((id) => visibleIds.has(id))
  )
}

/**
 * One card per indication, before grouping merges them: what each offer alone costs and whether
 * it is a firm quote. A package names single offers, and a grouped card keeps neither.
 */
export function indicationsToOfferCards(
  indications: InsuranceIndication[],
  /** As applicationToQuoteCards: false keeps a terms block off cards whose terms can't be shown. */
  carrierTermsAvailable = true,
  brokerName?: string
): QuoteCardData[] {
  const sourcedThrough = sourcedThroughByIndication(indications)
  return indications.map((indication) =>
    toQuoteCard(indication, brokerName, carrierTermsAvailable, sourcedThrough.get(indication.id))
  )
}

/**
 * The stage every indication would render as, including the ones the card list hides — a
 * Rocklake decline is withheld from the customer but is still a carrier that answered, so
 * page-level state has to count it. Stages only; never render off these.
 */
export function indicationStages(indications: InsuranceIndication[]): QuoteCardStage[] {
  return indications.map((indication) => toQuoteCard(indication).stage)
}
